import { useState } from 'react';
import { Navigate, useNavigate, Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { clientSignin, requestOtp } from '@shared/api/auth';
import { apiErrorMessage } from '@shared/api/client';
import { usePublicRedirect, ME_QUERY_KEY, roleHome } from '@shared/hooks/use-me';
import { useAuthStore } from '@shared/stores/auth-store';
import { getTokenStore } from '@shared/api/token-store';
import { useCooldown } from '@shared/hooks/use-cooldown';
import { validateEmail } from '@shared/utils/validation';
import { EyeIcon, EyeOffIcon } from '@/components/layout/icons';
import { AuthShell } from './auth-shell';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailTouched, setEmailTouched] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isOtpMode, setIsOtpMode] = useState(false);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const setSession = useAuthStore((s) => s.setSession);
  const { target, waiting } = usePublicRedirect();

  const signinMutation = useMutation({
    mutationFn: () => clientSignin({ email: email.trim(), password }),
    onSuccess: (session) => {
      getTokenStore().setAccessToken(session.accessToken, 'client');
      setSession(session.user);
      qc.setQueryData(ME_QUERY_KEY, {
        ...session.user,
        avatarUrl: null,
        emailVerifiedAt: null,
        lastLoginAt: null,
        createdAt: new Date().toISOString(),
      });
      qc.invalidateQueries({ queryKey: ME_QUERY_KEY });
      navigate(roleHome(session.user.role), { replace: true });
    },
  });

  const { secondsLeft, active: onCooldown, start: startCooldown } = useCooldown(30);
  const otpMutation = useMutation({
    mutationFn: (e: string) => requestOtp(e, 'CLIENT'),
    onSuccess: () => navigate(`/otp?email=${encodeURIComponent(email.trim())}`),
    onSettled: () => startCooldown(),
  });

  if (waiting) {
    return (
      <AuthShell>
        <div className="text-center text-sm text-gray-500">Loading…</div>
      </AuthShell>
    );
  }
  if (target) return <Navigate to={target} replace />;

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setEmailTouched(true);
    const result = validateEmail(email);
    if (!result.isValid) {
      setEmailError(result.error);
      return;
    }
    setEmailError(null);

    const emailVal = email.trim();
    if (isOtpMode) {
      if (onCooldown || otpMutation.isPending) return;
      otpMutation.mutate(emailVal);
    } else {
      if (signinMutation.isPending || !password) return;
      signinMutation.mutate();
    }
  }

  function handleEmailChange(val: string) {
    setEmail(val);
    if (emailTouched) {
      const result = validateEmail(val);
      setEmailError(result.isValid ? null : result.error);
    }
  }

  function handleEmailBlur() {
    setEmailTouched(true);
    if (email.trim()) {
      const result = validateEmail(email);
      setEmailError(result.isValid ? null : result.error);
    }
  }

  return (
    <AuthShell>
      <div className="space-y-6">
        <h1 className="text-center text-xl font-semibold leading-snug text-gray-900">
          India&rsquo;s #1 Platform for Legal &amp; Business Documentation
        </h1>

        <form className="space-y-4" onSubmit={onSubmit} noValidate>
          <div className="text-center text-sm text-gray-600">Client Log In</div>

          <div className="space-y-1">
            <label className="block text-xs font-medium text-gray-700">Email address</label>
            <Input
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => handleEmailChange(e.target.value)}
              onBlur={handleEmailBlur}
              className={emailError ? 'border-red-500 focus:border-red-600 focus:ring-red-500/30' : undefined}
              required
              autoFocus
            />
            {emailError && (
              <p className="mt-1 text-xs font-medium text-red-600">{emailError}</p>
            )}
          </div>

          {!isOtpMode && (
            <div className="space-y-1">
              <label className="block text-xs font-medium text-gray-700">Password</label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pr-10"
                  required
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOffIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>
          )}

          {isOtpMode ? (
            otpMutation.isError ? (
              <p className="text-sm text-red-600">{apiErrorMessage(otpMutation.error)}</p>
            ) : null
          ) : signinMutation.isError ? (
            <p className="text-sm text-red-600">{apiErrorMessage(signinMutation.error)}</p>
          ) : null}

          {isOtpMode ? (
            <Button type="submit" block disabled={otpMutation.isPending || onCooldown}>
              {otpMutation.isPending
                ? 'Sending…'
                : onCooldown
                  ? `Try again in ${secondsLeft}s`
                  : 'Send OTP'}
            </Button>
          ) : (
            <Button type="submit" block disabled={signinMutation.isPending}>
              {signinMutation.isPending ? 'Logging in…' : 'Log In'}
            </Button>
          )}

          <div className="text-center">
            <button
              type="button"
              className="text-xs text-brand-700 underline underline-offset-2 hover:text-brand-800"
              onClick={() => setIsOtpMode(!isOtpMode)}
            >
              {isOtpMode ? 'Use password instead' : 'Use OTP instead'}
            </button>
          </div>

          <p className="pt-2 text-center text-xs text-gray-500 border-t border-gray-100">
            Don&rsquo;t have an account?{' '}
            <Link to="/signup" className="text-brand-700 underline underline-offset-2">
              Sign up
            </Link>
          </p>
        </form>
      </div>
    </AuthShell>
  );
}
